// vendor libraries:
import React from 'react';
import Joyride, { Props as JoyrideProps, Step, TooltipRenderProps, CallBackProps, StoreHelpers, events as JoyrideEvents, EVENTS, STATUS, ACTIONS } from 'react-joyride';

import { ThemeProvider } from '@mui/material/styles';

// our stuff:
import { rvLightTheme } from '../common/themes';

import MaterialTooltip from './MaterialTooltip';

const ThemedMaterialTooltip = (props: TooltipRenderProps) => (
    <ThemeProvider theme={rvLightTheme}>
        <MaterialTooltip {...props} />
    </ThemeProvider>
);

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
const noop = function() : void {};

/** @brief Extends Step interface with "on" object where custom callbacks could be put for each step.
*/
export type QuickTourStep = Step & {
    on?: { [event in JoyrideEvents[keyof JoyrideEvents]]?: (event: QuickTourCallbackProps) => void };
};

/** @brief Extends CallBackProps interface, adding "step" to the callback.
*/
export type QuickTourCallbackProps = CallBackProps & {
    step: QuickTourStep;
};

/** @brief QuickTour helper supports only "reset" and "skip" functions.
*/
export type QuickTourHelpers = Pick<StoreHelpers, "reset" | "skip">;

/** @brief Props for the QuickTour component. */
export type QuickTourProps = Omit<JoyrideProps, "tooltipComponent" | "steps"> & {
    steps: QuickTourStep[];
    disableBeacon?: boolean;
    getHelpers?: (helpers: QuickTourHelpers) => void;
};

/** @brief QuickTour component built on top of react-joyride.
 *  Provides a guided tour with custom steps, callbacks, and controls.
 *  Supports disabling beacon, step-specific callbacks, and external helpers.
 */
function QuickTour({ steps, callback = noop, run = false, stepIndex = 0, getHelpers = noop, ...props }: Readonly<QuickTourProps>): JSX.Element {
    const [running, setRunning] = React.useState(run);
    const [currentStepIndex, setCurrentStepIndex] = React.useState(stepIndex);
    const [errored, setErrored] = React.useState(false);

    // quicktour controls
    const helpers: QuickTourHelpers = {
        reset: function (restart: boolean): void {
            setCurrentStepIndex(0);
            setRunning(restart);
        },
        skip: function(): void {
            setRunning(false);
            setCurrentStepIndex(0);
        },
    };

    // getHelpers is a callback returning "helper" object with some methods to control quicktour with
    getHelpers(helpers);

    // in Joyride disableBeacon prop is set only per step and cannot be set for all steps at once
    if (props.disableBeacon) {
        steps = steps.map(step => { step.disableBeacon = true; return step; });
    }

    const handleJoyrideCallback = function (event: QuickTourCallbackProps): void {
        const { action, index, status, type } = event;

        // it is reasonable to show target element on "step:before" and hide on "step:after"
        // unfortunately, Joyride checks for target element before running "step:before"
        // so we will try to invoke "step:before" action manually once
        // if it fails, we will move on to next step
        if (!errored && type === EVENTS.TARGET_NOT_FOUND && event.step.on && typeof event.step.on[EVENTS.STEP_BEFORE] === "function") {
            setErrored(true);
            event.step.on[EVENTS.STEP_BEFORE](event);
        }
        else {
            // invoke any callbacks for step events
            if (event.step.on) {
                // @ts-ignore
                const on = event.step.on[type];
                if (typeof on === "function")
                    on(event);
            }


            if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
                setErrored(false);
                setCurrentStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
            }
            else if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
                // set running state to false, so we can restart if we click start again.
                setErrored(false);
                setRunning(false);
            }
        }

        // console.groupCollapsed(`step #${index}: ${action} => ${type}`);
        // console.log(event);
        // console.groupEnd();

        // call external callback
        callback(event);
    };

    return <Joyride
        {...props}
        getHelpers={noop}
        run={running}
        stepIndex={currentStepIndex}
        steps={steps}
        callback={handleJoyrideCallback}
        tooltipComponent={ThemedMaterialTooltip}
        showSkipButton
        continuous
    />
}

export default QuickTour;